import React, { useCallback, useEffect, useState } from "react";
import { FiEdit3, FiTrash2 } from "react-icons/fi";
import api from "../api";
import { fmtCLP, fmtFecha } from "../utils/formato";
import toast from "../utils/toast";
import ConfirmModal from "../components/ConfirmModal";
import MovementModal from "../components/MovementModal";
import "./MovimientosCaja.css";

export default function MovimientosCaja({ usuario, cajaAbierta }) {
  const [movimientos, setMovimientos] = useState([]);
  const [cargando, setCargando] = useState(true);
  const [error, setError] = useState(null);
  const [mostrarForm, setMostrarForm] = useState(false);
  const [movimientoAEditar, setMovimientoAEditar] = useState(null);
  const [movimientoAEliminar, setMovimientoAEliminar] = useState(null);
  const [eliminandoId, setEliminandoId] = useState(null);
  const [desde, setDesde] = useState("");
  const [hasta, setHasta] = useState("");

  const cargar = useCallback(() => {
    setCargando(true);
    setError(null);
    const params = { limit: 100 };
    if (usuario?.rol !== "superadmin" && usuario?.id) params.usuario_id = usuario.id;
    if (desde) params.desde = desde;
    if (hasta) params.hasta = hasta;
    api.get("/api/caja/movimientos", { params })
      .then(({ data }) => setMovimientos(data?.rows || []))
      .catch((e) => setError(e?.response?.data?.error || e.message || "Error al cargar"))
      .finally(() => setCargando(false));
  }, [usuario?.id, usuario?.rol, desde, hasta]);

  useEffect(() => {
    cargar();
  }, [cargar]);

  const puedeCrearMovimiento = cajaAbierta === true;
  const puedeEliminarMovimientos = usuario?.rol === "superadmin";
  const tituloNuevoMovimiento = puedeCrearMovimiento
    ? "Registrar ingreso o egreso de efectivo"
    : cajaAbierta == null
      ? "Consultando estado de caja"
      : "Debe iniciar caja antes de registrar movimientos";

  const confirmarEliminarMovimiento = async () => {
    const movimiento = movimientoAEliminar;
    if (!movimiento) return;

    setMovimientoAEliminar(null);
    setEliminandoId(movimiento.id);
    setError(null);
    try {
      await api.delete(`/api/caja/movimientos/${movimiento.id}`);
      setMovimientos((prev) => prev.filter((m) => m.id !== movimiento.id));
      toast("Movimiento eliminado", "exito");
      cargar();
    } catch (e) {
      const mensaje = e?.response?.data?.error || e.message || "No se pudo eliminar el movimiento";
      setError(mensaje);
      toast(mensaje);
    } finally {
      setEliminandoId(null);
    }
  };

  return (
    <div className="mc-page">
      <header className="mc-header">
        <h1>Movimientos de caja</h1>
        <p className="mc-desc">
          {usuario?.rol === "superadmin"
            ? "Ingresos y egresos de efectivo de todos los usuarios."
            : "Tus ingresos y egresos de efectivo adicionales al fondo inicial."}
        </p>
      </header>

      <div className="mc-filters">
        <label>
          Desde
          <input type="date" value={desde} onChange={(e) => setDesde(e.target.value)} />
        </label>
        <label>
          Hasta
          <input type="date" value={hasta} onChange={(e) => setHasta(e.target.value)} />
        </label>
        <button
          type="button"
          className="mc-btn primary"
          onClick={() => puedeCrearMovimiento && setMostrarForm(true)}
          disabled={!puedeCrearMovimiento}
          title={tituloNuevoMovimiento}
        >
          + Nuevo movimiento
        </button>
      </div>

      {error && <div className="mc-alert error">{error}</div>}

      {mostrarForm && (
        <MovementModal
          usuario={usuario}
          onCerrar={() => setMostrarForm(false)}
          onGuardado={() => { setMostrarForm(false); cargar(); }}
        />
      )}

      {movimientoAEditar && (
        <MovementModal
          usuario={usuario}
          movimiento={movimientoAEditar}
          onCerrar={() => setMovimientoAEditar(null)}
          onGuardado={(movimientoActualizado) => {
            if (movimientoActualizado?.id) {
              setMovimientos((prev) => prev.map((m) => (
                m.id === movimientoActualizado.id
                  ? { ...m, ...movimientoActualizado, usuario_nombre: m.usuario_nombre }
                  : m
              )));
            }
            setMovimientoAEditar(null);
            cargar();
          }}
        />
      )}

      {movimientoAEliminar && (
        <ConfirmModal
          mensaje={`Eliminar ${movimientoAEliminar.tipo === "ingreso" ? "ingreso" : "egreso"} de ${fmtCLP(movimientoAEliminar.monto)}? Esta accion no se puede deshacer.`}
          textoConfirmar="Eliminar"
          onConfirmar={confirmarEliminarMovimiento}
          onCancelar={() => setMovimientoAEliminar(null)}
        />
      )}

      {cargando ? (
        <div className="mc-empty">Cargando movimientos…</div>
      ) : !movimientos.length ? (
        <div className="mc-empty">No hay movimientos en el periodo.</div>
      ) : (
        <div className="mc-table-wrap">
          <table className="mc-table">
            <thead>
              <tr>
                <th>Fecha / Hora</th>
                <th>Usuario</th>
                <th>Tipo</th>
                <th className="right">Monto</th>
                <th>Concepto</th>
                <th className="center">Accion</th>
              </tr>
            </thead>
            <tbody>
              {movimientos.map((m) => (
                <tr key={m.id} className={m.tipo === "egreso" ? "mc-row-egreso" : ""}>
                  <td>{fmtFecha(m.creado_en)}</td>
                  <td>{m.usuario_nombre ?? `ID ${m.usuario_id}`}</td>
                  <td>{m.tipo === "ingreso" ? "Ingreso" : "Egreso"}</td>
                  <td className="right">{m.tipo === "ingreso" ? "+" : "−"} {fmtCLP(m.monto)}</td>
                  <td>{m.concepto || "—"}</td>
                  <td className="center">
                    <div className="mc-actions">
                      <button
                        type="button"
                        className="mc-icon-btn"
                        onClick={() => setMovimientoAEditar(m)}
                        title="Editar movimiento"
                        aria-label="Editar movimiento"
                      >
                        <FiEdit3 aria-hidden="true" />
                      </button>
                      {puedeEliminarMovimientos && (
                        <button
                          type="button"
                          className="mc-icon-btn danger"
                          onClick={() => setMovimientoAEliminar(m)}
                          disabled={eliminandoId === m.id}
                          title="Eliminar movimiento"
                          aria-label="Eliminar movimiento"
                        >
                          <FiTrash2 aria-hidden="true" />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
